import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BellIcon, 
  UserPlusIcon, 
  TruckIcon, 
  CurrencyDollarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { collection, query, orderBy, limit, onSnapshot, where } from 'firebase/firestore';
import { db } from '@/config/firebase';

interface Notification {
  id: string;
  type: 'customer_signup' | 'shipment_created' | 'invoice_updated' | 'payment_received';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  data?: any;
}

export default function NotificationPanel() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    // Listen for customer signups
    const customersQuery = query(
      collection(db, 'users'),
      where('role', '==', 'customer'),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const shipmentsQuery = query(
      collection(db, 'shipments'),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const invoicesQuery = query(
      collection(db, 'invoices'),
      orderBy('updatedAt', 'desc'),
      limit(10)
    );

    const unsubscribeCustomers = onSnapshot(customersQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const customer = change.doc.data();
          addNotification({
            id: `customer_${change.doc.id}`,
            type: 'customer_signup',
            title: 'New Customer Signup',
            message: `${customer.name || customer.email} has signed up`,
            timestamp: customer.createdAt?.toDate() || new Date(),
            read: false,
            data: { customerId: change.doc.id, customer }
          });
        }
      });
    });

    const unsubscribeShipments = onSnapshot(shipmentsQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const shipment = change.doc.data();
          // Only show customer-created shipments
          if (shipment.createdBy && shipment.createdBy === shipment.customerId) {
            addNotification({
              id: `shipment_${change.doc.id}`,
              type: 'shipment_created',
              title: 'New Customer Shipment',
              message: `Customer created shipment ${shipment.trackingNumber}`,
              timestamp: shipment.createdAt?.toDate() || new Date(),
              read: false,
              data: { shipmentId: change.doc.id, shipment }
            });
          }
        }
      });
    });

    const unsubscribeInvoices = onSnapshot(invoicesQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'modified') {
          const invoice = change.doc.data();
          const oldData = change.doc.metadata.hasPendingWrites ? null : change.doc.data();
          
          // Check if status changed to pending (payment received)
          if (invoice.status === 'pending' && oldData?.status !== 'pending') {
            addNotification({
              id: `payment_${change.doc.id}`,
              type: 'payment_received',
              title: 'Payment Received',
              message: `Payment received for invoice ${invoice.invoiceNumber}`,
              timestamp: new Date(),
              read: false,
              data: { invoiceId: change.doc.id, invoice }
            });
          }
        }
      });
    });

    return () => {
      unsubscribeCustomers();
      unsubscribeShipments();
      unsubscribeInvoices();
    };
  }, [isOpen]);

  const addNotification = (notification: Notification) => {
    setNotifications(prev => {
      // Check if notification already exists
      const exists = prev.find(n => n.id === notification.id);
      if (exists) return prev;
      
      const newNotifications = [notification, ...prev].slice(0, 50); // Keep last 50
      setUnreadCount(newNotifications.filter(n => !n.read).length);
      return newNotifications;
    });
  };

  const markAsRead = (notificationId: string) => {
    setNotifications(prev => 
      prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'customer_signup':
        return <UserPlusIcon className="h-5 w-5 text-green-600" />;
      case 'shipment_created':
        return <TruckIcon className="h-5 w-5 text-blue-600" />;
      case 'payment_received':
        return <CurrencyDollarIcon className="h-5 w-5 text-yellow-600" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getNotificationLink = (notification: Notification) => {
    switch (notification.type) {
      case 'customer_signup':
        return `/customers/${notification.data?.customerId}`;
      case 'shipment_created':
        return `/shipments/${notification.data?.shipmentId}`;
      case 'payment_received':
        return `/invoices/${notification.data?.invoiceId}`;
      default:
        return '#';
    }
  };

  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <BellIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p>No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      !notification.read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <Link
                      to={getNotificationLink(notification)}
                      onClick={() => markAsRead(notification.id)}
                      className="block"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            {formatTimeAgo(notification.timestamp)}
                          </p>
                        </div>
                        {!notification.read && (
                          <div className="flex-shrink-0">
                            <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-4 border-t border-gray-200">
              <Link
                to="/notifications"
                className="text-sm text-blue-600 hover:text-blue-800 block text-center"
              >
                View all notifications
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
} 